<template>
  <div
    v-if="hasReactions"
    class="s-messageReactions">
    <div
      v-for="(reaction, index) in groupedReactions"
      :key="index"
      class="s-messageReactions__item"
      :class="{
        '-own': reaction.isOwn,
      }"
      :role="reaction.isOwn ? 'button' : undefined"
      :tabindex="reaction.isOwn ? 0 : undefined"
      @click="handleReactionClick(reaction)"
      @keydown.enter="handleReactionClick(reaction)"
      @keydown.space.prevent="handleReactionClick(reaction)">
      <span class="s-messageReactions__emoji">{{ reaction.emoji }}</span>
      <img
        v-if="reaction.avatarUrl"
        :src="reaction.avatarUrl"
        :alt="reaction.userName"
        class="s-messageReactions__avatar" />
      <span
        v-else
        class="s-messageReactions__avatarPlaceholder">
        {{ reaction.userInitial }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

import api from '@/logic/api'
import { useChatStore } from '@/modules/chat/chatStore'
import { useChatThreadStore } from '@/modules/chat/chatThreadStore'
import { parseReactions, removeReaction, removeReactionFromBackend } from '@/modules/chat/logic/reactions'
import store from '@/store'

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
})

const chatStore = useChatStore()

const currentUserId = computed(() => store.getters['auth/userId'])

/**
 * Get all reactions parsed from message metadata
 */
const reactions = computed(() => parseReactions(props.message))

/**
 * Check if message has any reactions
 */
const hasReactions = computed(() => reactions.value.length > 0)

/**
 * Get participant info from chat thread
 */
const participant = computed(() => chatStore.chatThread?.participant)

/**
 * Group reactions with user info for display
 */
const groupedReactions = computed(() => {
  return reactions.value.map(([userId, emoji]) => {
    const isOwn = Number(userId) === Number(currentUserId.value)
    let avatarUrl = null
    let userName = ''
    let userInitial = '?'

    if (isOwn) {
      // Current user's avatar
      avatarUrl = store.getters['auth/avatar']
      userName = store.getters['auth/displayname'] || api.i18n.t('chat.you')
      userInitial = userName.charAt(0).toUpperCase()
    } else if (participant.value && Number(participant.value.user_id) === Number(userId)) {
      // Chat partner's avatar
      avatarUrl = participant.value.avatar_url
      userName = participant.value.displayName || ''
      userInitial = userName.charAt(0).toUpperCase()
    }

    return {
      emoji,
      userId,
      isOwn,
      avatarUrl,
      userName,
      userInitial,
    }
  })
})

/**
 * Handle click on a reaction - remove if own reaction
 */
async function handleReactionClick(reaction) {
  if (!reaction.isOwn) {
    return
  }

  const chatThreadStore = useChatThreadStore(chatStore.chatThread.azure_chat_thread_id)
  const newReactions = removeReaction(props.message, reaction.emoji)

  // Optimistic update
  chatThreadStore.updateMessageByAzureId(props.message.id.toString(), {
    metadata: {
      ...props.message.metadata,
      reactions: JSON.stringify(newReactions),
    },
  })

  // Save to backend
  const success = await removeReactionFromBackend(
    chatStore.chatThread.id,
    props.message.id.toString(),
    reaction.emoji,
  )

  // Rollback if failed
  if (!success) {
    chatThreadStore.updateMessageByAzureId(props.message.id.toString(), {
      metadata: {
        ...props.message.metadata,
        reactions: JSON.stringify(reactions.value),
      },
    })
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/config';

.s-messageReactions {
  display: flex;
  flex-wrap: wrap;
  gap: $spacing-1;
  // Leave space for the timestamp that's positioned absolutely at bottom-right
  padding-inline-end: 60px;
  // Ensure reactions grow horizontally before wrapping (prevents stacking on short messages like "hi")
  min-width: 150px;
}

.s-messageReactions__item {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 6px;
  border-radius: 12px;
  background-color: $color-light-gray;
  font-size: 14px;
  line-height: 1;

  &.-own {
    cursor: pointer;
    // Same color as the chat message send button
    background-color: $color-primary;
    color: $color-white;

    &:hover {
      opacity: 0.85;
    }
  }
}

.s-messageReactions__emoji {
  font-size: 16px;
}

.s-messageReactions__avatar {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  object-fit: cover;
}

.s-messageReactions__avatarPlaceholder {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: $color-gray;
  color: $color-white;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
